import React, { useRef } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { COVER_IMAGE_SQUARE_WIDTH } from '../eventFormConstants';
import { BRAND_ORANGE } from './manageEventFieldStyles';

const CoverImageOptimizingBlock = ({
  error = '',
  onCancel,
  onRetry,
  accept = 'image/jpeg,image/jpg,image/png',
  compact = false,
  sx,
}) => {
  const inputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file && onRetry) {
      void onRetry(file);
    }
  };

  return (
    <Box
      sx={[
        {
          width: COVER_IMAGE_SQUARE_WIDTH,
          maxWidth: '100%',
          minHeight: compact ? 180 : 220,
          border: error ? '1px solid' : '2px dashed',
          borderColor: error ? 'error.light' : BRAND_ORANGE,
          borderRadius: '10px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: compact ? 1 : 1.25,
          px: 2.5,
          py: 2.5,
          bgcolor: error ? 'error.light' : 'background.paper',
          overflow: 'hidden',
          boxSizing: 'border-box',
        },
        sx,
      ]}
    >
      {!error ? (
        <>
          <Box
            sx={{
              width: compact ? 44 : 52,
              height: compact ? 44 : 52,
              borderRadius: '50%',
              bgcolor: 'rgba(224, 104, 10, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CircularProgress
              size={compact ? 24 : 28}
              thickness={4}
              sx={{ color: BRAND_ORANGE, display: 'block' }}
            />
          </Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: compact ? '0.8125rem' : '0.875rem',
              color: 'text.primary',
              textAlign: 'center',
            }}
          >
            Optimizing image…
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'center', lineHeight: 1.45, maxWidth: 220 }}
          >
            This may take up to 2 minutes. You can keep filling out the rest of the form.
          </Typography>
        </>
      ) : (
        <>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: compact ? '0.8125rem' : '0.875rem',
              color: 'error.main',
              textAlign: 'center',
            }}
          >
            Optimization failed
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'center', lineHeight: 1.45 }}
          >
            {error}
          </Typography>
          {onRetry ? (
            <>
              <Button
                type="button"
                variant="contained"
                size="small"
                onClick={() => inputRef.current?.click()}
                sx={{
                  mt: 0.5,
                  textTransform: 'none',
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                Upload again
              </Button>
              <input
                ref={inputRef}
                type="file"
                accept={accept}
                hidden
                onChange={handleFileChange}
              />
            </>
          ) : null}
        </>
      )}

      {onCancel ? (
        <Button
          type="button"
          size="small"
          onClick={onCancel}
          sx={{ textTransform: 'none', color: 'text.secondary', flexShrink: 0 }}
        >
          {error ? 'Restore previous' : 'Cancel'}
        </Button>
      ) : null}
    </Box>
  );
};

export default CoverImageOptimizingBlock;
